import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Filter, 
  SortAsc,
  Circle,
  CheckCircle2,
  Clock,
  Tag,
  Calendar,
  MoreVertical,
  Edit,
  Trash2,
  Play,
  Pause,
  ChevronRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import { todosAPI } from '../utils/api';
import { formatRelativeTime, getTimeRemaining, formatDuration } from '../utils/dateUtils';
import TodoModal from '../components/TodoModal';
import { motion, AnimatePresence } from 'framer-motion';

const TodoList = ({ categories, tags, onCategoryUpdate, onTagUpdate }) => {
  const { view } = useParams();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');

  const [todos, setTodos] = useState([]);
  const [filteredTodos, setFilteredTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTodos, setExpandedTodos] = useState(new Set());

  useEffect(() => {
    loadTodos();
  }, [view, categoryParam]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [todos, filterStatus, sortBy, searchQuery]);

  const loadTodos = async () => {
    try {
      setLoading(true);
      let params = {};

      if (categoryParam) {
        params.categoryId = categoryParam;
      } else if (view) {
        // Map URL segments to backend enum values
        const timeframeMap = {
          'today': 'today',
          'this-week': 'week',
          'this-month': 'month',
          'this-quarter': 'quarter'
        };
        params.timeframe = timeframeMap[view] || view;
      }

      const res = await todosAPI.getAll(params);
      setTodos(res.data);
    } catch (error) {
      console.error('Error loading todos:', error);
      toast.error('Failed to load todos');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...todos];

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(todo => todo.status === filterStatus);
    }

    // Apply search
    if (searchQuery) {
      filtered = filtered.filter(todo =>
        todo.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (todo.description && todo.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'createdAt':
          return b.createdAt - a.createdAt;
        case 'dueDate':
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate) - new Date(b.dueDate);
        case 'priority':
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    setFilteredTodos(filtered);
  };

  const handleCreateTodo = async (todoData) => {
    try {
      const res = await todosAPI.create(todoData);
      setTodos([res.data, ...todos]);
      toast.success('Todo created successfully!');
      setShowModal(false);
    } catch (error) {
      console.error('Error creating todo:', error);
      toast.error('Failed to create todo');
    }
  };

  const handleUpdateTodo = async (id, updates) => {
    try {
      const res = await todosAPI.update(id, updates);
      setTodos(todos.map(todo => todo.id === id ? res.data : todo));
      toast.success('Todo updated successfully!');
      setShowModal(false);
      setEditingTodo(null);
    } catch (error) {
      console.error('Error updating todo:', error);
      toast.error('Failed to update todo');
    }
  };

  const handleDeleteTodo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) return;

    try {
      await todosAPI.delete(id);
      setTodos(todos.filter(todo => todo.id !== id));
      toast.success('Todo deleted successfully!');
    } catch (error) {
      console.error('Error deleting todo:', error);
      toast.error('Failed to delete todo');
    }
  };

  const handleToggleStatus = async (todo) => {
    const newStatus = todo.status === 'completed' ? 'pending' : 'completed';
    await handleUpdateTodo(todo.id, { status: newStatus });
  };

  const handleToggleExpand = (todoId) => {
    setExpandedTodos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(todoId)) {
        newSet.delete(todoId);
      } else {
        newSet.add(todoId);
      }
      return newSet;
    });
  };

  const handleToggleSubtask = async (todoId, subtaskId) => {
    const todo = todos.find(t => t.id === todoId);
    if (!todo) return;

    const updatedSubtasks = todo.subtasks.map(subtask =>
      subtask.id === subtaskId
        ? { ...subtask, completed: !subtask.completed }
        : subtask
    );

    await handleUpdateTodo(todoId, { subtasks: updatedSubtasks });
  };

  const handleStartProgress = async (todo) => {
    const newStatus = todo.status === 'in-progress' ? 'pending' : 'in-progress';
    await handleUpdateTodo(todo.id, { status: newStatus });
  };

  const getPageTitle = () => {
    if (categoryParam) {
      const category = categories.find(c => c.id === categoryParam);
      return category ? category.name : 'Category';
    }
    
    switch (view) {
      case 'today': return 'Today';
      case 'this-week': return 'This Week';
      case 'this-month': return 'This Month';
      case 'this-quarter': return 'This Quarter';
      default: return 'All Todos';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#64748b';
    }
  };

  const TodoItem = ({ todo }) => {
    const timeRemaining = todo.dueDate ? getTimeRemaining(todo.dueDate) : null;
    const category = categories.find(c => c.id === todo.categoryId);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -100 }}
        className={`todo-item ${todo.status === 'completed' ? 'completed' : ''}`}
      >
        <div className="todo-checkbox" onClick={() => handleToggleStatus(todo)}>
          {todo.status === 'completed' ? (
            <CheckCircle2 size={24} className="completed-icon" />
          ) : (
            <Circle size={24} />
          )}
        </div>

        <div className="todo-content">
          <div className="todo-header">
            <h3>{todo.title}</h3>
            <div 
              className="priority-indicator" 
              style={{ backgroundColor: getPriorityColor(todo.priority) }}
              title={`${todo.priority} priority`}
            />
          </div>

          {todo.description && (
            <p className="todo-description">{todo.description}</p>
          )}

          <div className="todo-meta">
            {category && (
              <span className="meta-badge" style={{ backgroundColor: `${category.color}15`, color: category.color }}>
                {category.name}
              </span>
            )}

            {todo.dueDate && (
              <span className={`meta-badge ${timeRemaining?.isOverdue ? 'overdue' : ''}`}>
                <Calendar size={14} />
                {timeRemaining?.isOverdue ? 'Overdue' : formatRelativeTime(todo.dueDate)}
              </span>
            )}

            {todo.estimatedTime && (
              <span className="meta-badge">
                <Clock size={14} />
                {formatDuration(todo.estimatedTime)}
              </span>
            )}

            {todo.tags && todo.tags.length > 0 && (
              <span className="meta-badge">
                <Tag size={14} />
                {todo.tags.length} tags
              </span>
            )}

            {todo.progress > 0 && todo.status !== 'completed' && (
              <span className="meta-badge progress">
                {todo.progress}% complete
              </span>
            )}
          </div>

          {todo.subtasks && todo.subtasks.length > 0 && (
            <div 
              className="subtasks-preview clickable"
              onClick={() => handleToggleExpand(todo.id)}
            >
              <span>{todo.completedSubtasks}/{todo.subtasks.length} subtasks completed</span>
              <ChevronRight 
                size={16} 
                className={`expand-icon ${expandedTodos.has(todo.id) ? 'expanded' : ''}`}
              />
            </div>
          )}
        </div>

        {expandedTodos.has(todo.id) && todo.subtasks && todo.subtasks.length > 0 && (
          <div className="subtasks-expanded">
            <h4>Subtasks</h4>
            <div className="subtasks-list">
              {todo.subtasks.map(subtask => (
                <div key={subtask.id} className="subtask-item">
                  <div 
                    className="subtask-checkbox"
                    onClick={() => handleToggleSubtask(todo.id, subtask.id)}
                  >
                    {subtask.completed ? (
                      <CheckCircle2 size={16} className="completed-icon" />
                    ) : (
                      <Circle size={16} />
                    )}
                  </div>
                  <span className={subtask.completed ? 'completed' : ''}>
                    {subtask.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="todo-actions">
          <button 
            className={`action-btn ${todo.status === 'in-progress' ? 'active' : ''}`}
            onClick={() => handleStartProgress(todo)}
            title={todo.status === 'in-progress' ? 'Pause' : 'Start'}
          >
            {todo.status === 'in-progress' ? <Pause size={18} /> : <Play size={18} />}
          </button>

          <button 
            className="action-btn"
            onClick={() => {
              setEditingTodo(todo);
              setShowModal(true);
            }}
          >
            <Edit size={18} />
          </button>

          <button 
            className="action-btn danger"
            onClick={() => handleDeleteTodo(todo.id)}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return <div className="page-loading">Loading todos...</div>;
  }

  return (
    <div className="todolist-page">
      <div className="page-header">
        <h1>{getPageTitle()}</h1>
        <button 
          className="btn-primary"
          onClick={() => {
            setEditingTodo(null);
            setShowModal(true);
          }}
        >
          <Plus size={20} />
          Add Todo
        </button>
      </div>

      <div className="todolist-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search todos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filters">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>

          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="createdAt">Sort by: Created</option>
            <option value="dueDate">Sort by: Due Date</option>
            <option value="priority">Sort by: Priority</option>
            <option value="title">Sort by: Title</option>
          </select>
        </div>
      </div>

      <div className="todos-container">
        <AnimatePresence>
          {filteredTodos.length === 0 ? (
            <div className="empty-state">
              <CheckCircle2 size={64} />
              <h2>No todos found</h2>
              <p>Create your first todo to get started!</p>
              <button 
                className="btn-primary"
                onClick={() => setShowModal(true)}
              >
                <Plus size={20} />
                Create Todo
              </button>
            </div>
          ) : (
            filteredTodos.map(todo => (
              <TodoItem key={todo.id} todo={todo} />
            ))
          )}
        </AnimatePresence>
      </div>

      {showModal && (
        <TodoModal
          todo={editingTodo}
          categories={categories}
          tags={tags}
          onSave={editingTodo ? 
            (data) => handleUpdateTodo(editingTodo.id, data) : 
            handleCreateTodo
          }
          onClose={() => {
            setShowModal(false);
            setEditingTodo(null);
          }}
        />
      )}
    </div>
  );
};

export default TodoList;
